import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Copy, Trophy, Sparkles, Share2, Flame } from "lucide-react";
import { toast } from "sonner";

/**
 * Referral engine page: shows the user's referral code + shareable links,
 * their earnings, and a public leaderboard of top recruiters.
 */
export default function Referrals() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState(null);
  const [boost, setBoost] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/referrals/me").then((r) => setMe(r.data)).catch(() => {});
    }
    api.get("/referrals/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/referrals/boost").then((r) => setBoost(r.data)).catch(() => {});
  }, [user]);

  const prize = boost?.prize_eur || board?.prize_eur || 10;
  const boostActive = !!boost?.active;
  const hoursLeft = boost?.hours_left || 0;

  const copy = (text, label = "Link") => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const whatsappShare = (url) => {
    const boostLine = boostActive ? ` €${prize} DOUBLE BOUNTY on for ${hoursLeft}h.` : "";
    const msg = encodeURIComponent(
      `Just joined SiteCrews.net — Ireland + UK trades marketplace, no agency fees.${boostLine} Get on the board free → ${url}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  return (
    <div data-testid="referrals-page" className="min-h-screen bg-neutral-950">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-[2px] w-10 bg-amber-500" />
          <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">
            Refer &amp; earn
          </span>
        </div>

        {boostActive && (
          <div data-testid="boost-banner" className="mb-6 border-2 border-amber-500 bg-amber-500/10 px-5 py-4 flex items-center gap-3">
            <Flame className="w-6 h-6 text-amber-500 shrink-0 animate-pulse" />
            <div className="min-w-0">
              <div className="display text-xl text-amber-500 leading-none">
                DOUBLE BOUNTY IS LIVE — €{prize} PER SIGNUP
              </div>
              <div className="text-xs uppercase tracking-widest text-amber-200/80 mt-1">
                {hoursLeft > 0 ? `${hoursLeft}h left` : "Ends soon"} · Back to €{boost?.base_prize_eur || 10} after
              </div>
            </div>
          </div>
        )}

        <h1 className="display text-4xl md:text-6xl">
          Bring a mate. <span className="text-amber-500">€{prize}</span> per signup.
        </h1>
        <p className="text-neutral-300 mt-4 max-w-lg text-lg">
          Every real signup you refer earns you €{prize}{boostActive ? ` (double bounty on — normal rate €${boost?.base_prize_eur || 10})` : ""}. Cash out via
          contact reveals, boost credits, or Founding Member discount. No cap.
        </p>

        {user && me && (
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <StatCard label="Signups you brought" value={me.referred_count || 0} />
            <StatCard label="You've earned" value={`€${me.earned_eur || 0}`} highlight />
            <StatCard label="Your code" value={me.code || "—"} mono />
          </div>
        )}

        {user && me?.share_url && (
          <div className="mt-8 space-y-3">
            <ShareRow
              label="For tradies (Get Work link)"
              url={me.share_url}
              onCopy={() => copy(me.share_url, "Share link")}
              onWa={() => whatsappShare(me.share_url)}
            />
            <ShareRow
              label="For contractors (Hire Crew link)"
              url={me.share_hire_url}
              onCopy={() => copy(me.share_hire_url, "Share link")}
              onWa={() => whatsappShare(me.share_hire_url)}
            />
          </div>
        )}

        {!user && (
          <div className="mt-8 bg-neutral-900 border border-neutral-800 p-6 text-sm text-neutral-400">
            Sign in or create an account to get your referral code.
          </div>
        )}

        {/* Leaderboard */}
        <div className="mt-16">
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="w-5 h-5 text-amber-500" />
            <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">
              Top recruiters this week
            </span>
          </div>
          {!board?.leaders?.length ? (
            <div className="bg-neutral-900 border border-neutral-800 p-6 text-sm text-neutral-500">
              Board is empty. Be the first — refer someone today and lock the top spot.
            </div>
          ) : (
            <div className="bg-neutral-900 border border-neutral-800 divide-y divide-neutral-800">
              {board.leaders.map((row, i) => (
                <div
                  key={i}
                  data-testid={`leaderboard-row-${i}`}
                  className="flex items-center justify-between px-5 py-4"
                >
                  <div className="flex items-center gap-4">
                    <span
                      className={`display text-2xl ${
                        i === 0 ? "text-amber-500" : "text-neutral-600"
                      }`}
                    >
                      #{i + 1}
                    </span>
                    <div>
                      <div className="font-bold">{row.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                        {row.role === "employer" ? "Contractor" : "Tradesperson"}
                        {row.county ? ` · ${row.county}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="display text-2xl text-amber-500">
                      €{(row.referred_count * (board.prize_eur || 10))}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {row.referred_count} signup{row.referred_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight, mono }) {
  return (
    <div
      className={`p-5 border ${
        highlight ? "border-amber-500 bg-amber-500/10" : "border-neutral-800 bg-neutral-900"
      }`}
    >
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      <div className={`display text-3xl ${highlight ? "text-amber-500" : "text-white"} ${mono ? "font-mono text-2xl" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function ShareRow({ label, url, onCopy, onWa }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
        <div className="text-sm text-neutral-300 font-mono truncate">{url}</div>
      </div>
      <div className="flex gap-2">
        <button
          data-testid={`copy-${label.includes("tradies") ? "worker" : "employer"}`}
          onClick={onCopy}
          className="inline-flex items-center gap-2 bg-neutral-950 border border-neutral-800 text-neutral-300 px-3 py-2 text-xs uppercase tracking-widest hover:border-amber-500 hover:text-amber-500 transition-colors"
        >
          <Copy className="w-3 h-3" />
          Copy
        </button>
        <button
          data-testid={`wa-${label.includes("tradies") ? "worker" : "employer"}`}
          onClick={onWa}
          className="inline-flex items-center gap-2 bg-amber-500 text-neutral-950 px-3 py-2 text-xs uppercase tracking-widest font-bold hover:bg-amber-400 transition-colors"
        >
          <Share2 className="w-3 h-3" />
          WhatsApp
        </button>
      </div>
    </div>
  );
}
